<template>
  <DialogHeader>Share technical comparison analysis report</DialogHeader>
  <DialogContent>
    <div class="flex items-center bg-[#F9F9FA] rounded-md px-3 py-2.5 mb-3.5">
      <div class="flex items-center flex-1 min-w-0">
        <div
          class="min-w-[50px] max-w-[50px] h-[50px] rounded-[3px] bg-gray-200/25 overflow-hidden mr-2.5 flex items-center justify-center"
        >
          <img
            v-if="copyrightImage"
            :src="copyrightImage"
            class="w-full h-full object-cover"
          />
          <ThumbnailIcon v-else :type="report.copyright.type" :icon-size="34" />
        </div>
        <div class="min-w-0 w-full" v-truncate>
          <div
            class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-1"
          >
            Your registered work
          </div>
          <div
            data-truncate-target
            class="font-primary font-medium text-[14px] leading-[18px] text-gray-800"
          >
            {{ copyrightName }}
          </div>
        </div>
      </div>

      <div class="shrink-0 mx-3 flex items-center justify-center">
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 6 L16 6 M13 3 L16 6 L13 9"
            stroke="#292C30"
            stroke-width="1.7"
            stroke-linecap="square"
            fill="none"
          />
          <path
            d="M16 12 L2 12 M5 9 L2 12 L5 15"
            stroke="#292C30"
            stroke-width="1.7"
            stroke-linecap="square"
            fill="none"
          />
        </svg>
      </div>

      <div class="flex items-center flex-1 min-w-0">
        <div
          class="min-w-[50px] max-w-[50px] h-[50px] rounded-[3px] bg-gray-200/25 overflow-hidden mr-2.5 flex items-center justify-center"
        >
          <img
            v-if="infringingImage"
            :src="infringingImage"
            class="w-full h-full object-cover"
          />
          <ThumbnailIcon
            v-else
            :type="report.infringingFile.type"
            :icon-size="34"
          />
        </div>
        <div class="min-w-0 w-full" v-truncate>
          <div
            class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-1"
          >
            Potential infringing work
          </div>
          <div
            data-truncate-target
            class="font-primary font-medium text-[14px] leading-[18px] text-gray-800"
          >
            {{ infringingName }}
          </div>
        </div>
      </div>
    </div>

    <SearchMultipleUsers
      v-model="usersToInvite"
      :show-own-entity="true"
      class="mb-3.5"
    />

    <div class="flex justify-end mb-3.5">
      <UiButton
        primary
        :disabled="!usersToInvite.length"
        :loading="sendInviteLoading"
        @click="handleSendInvite"
      >
        Send invite
      </UiButton>
    </div>

    <Table.Base v-if="viewerEdges.length">
      <Table.Headers>
        <Table.Header> Name </Table.Header>
        <Table.Header class="w-[50px]"> </Table.Header>
      </Table.Headers>
      <Table.Row
        v-for="edge in viewerEdges"
        :key="getViewerKey(edge.node)"
        class="hover:bg-transparent! cursor-default!"
      >
        <Table.Cell class="h-[60px]!">
          <Avatar
            v-if="!isEmailInvite(edge.node)"
            :src="getViewerAvatarUrl(edge.node)"
            :size="32"
            class="mr-2.5 shrink-0"
          />
          <div
            v-else
            class="min-w-8 max-w-8 h-8 mr-2.5 flex items-center justify-center"
          >
            <Icon name="Send" :size="22" class="stroke-gray-800" />
          </div>
          <div v-truncate class="w-full">
            <div
              data-truncate-target
              class="font-primary font-medium text-[14px] leading-[18px] text-gray-800"
            >
              {{ getViewerName(edge.node) }}
            </div>
            <div
              v-if="getViewerSecondaryText(edge.node)"
              class="font-secondary text-[13px] leading-[18px] text-gray-500"
            >
              {{ getViewerSecondaryText(edge.node) }}
            </div>
          </div>
        </Table.Cell>
        <Table.Cell class="h-[60px]! justify-end gap-x-2">
          <div
            v-if="isEmailInvite(edge.node)"
            class="font-secondary text-[14px] leading-[20px] text-gray-500"
          >
            Invited
          </div>
          <Float.Base>
            <Float.Trigger>
              <div
                class="w-[30px] h-[30px] rounded-sm hover:bg-gray-800/6 flex items-center justify-center cursor-pointer"
              >
                <Icon
                  name="ThreeDots"
                  :size="18"
                  class="stroke-gray-800 fill-gray-800"
                />
              </div>
            </Float.Trigger>
            <Float.Component>
              <MenuList>
                <MenuListItem
                  class="stroke-red-700!"
                  :disabled="removingViewerKey === getViewerKey(edge.node)"
                  @click="handleRemove(edge.node)"
                >
                  <Icon name="Delete" :size="18" class="stroke-red-700 mr-3" />
                  <div class="text-red-700">Remove</div>
                </MenuListItem>
              </MenuList>
            </Float.Component>
          </Float.Base>
        </Table.Cell>
      </Table.Row>
    </Table.Base>

    <div class="bg-[#F9F9FA] rounded-md px-3 pt-[15px] pb-2.5 mt-3.5">
      <div class="flex items-center justify-between">
        <div class="flex-1 min-w-0">
          <div
            class="flex items-center font-primary font-semibold text-[14px] leading-[18px] text-gray-800"
          >
            <Icon
              name="Link"
              :size="14"
              class="stroke-gray-800 mr-2 shrink-0"
            />
            <div>Public link</div>
          </div>
          <div
            class="font-secondary text-[14px] leading-[18px] text-gray-800 mt-0.5"
          >
            Public links do not contain playable media.
          </div>
        </div>
        <Toggle
          :model-value="publicLinkEnabled"
          :disabled="updateSettingsLoading"
          class="ml-3 shrink-0"
          @update:modelValue="handlePublicLinkToggle"
        />
      </div>

      <div
        v-if="publicLinkEnabled"
        class="flex items-center bg-white border border-[#D4D8E1] rounded-md h-[41px] pl-[14px] pr-[6px] mt-2.5"
      >
        <div
          class="flex-1 min-w-0 truncate font-secondary text-[14px] leading-[18px] text-gray-800"
        >
          {{ publicUrl }}
        </div>
        <UiButton primary class="ml-2 shrink-0" @click="handleCopyLink">
          {{ copied ? 'Copied' : 'Copy link' }}
        </UiButton>
      </div>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useApolloClient, useMutation } from '@vue/apollo-composable'
import {
  Avatar,
  Icon,
  Table,
  ThumbnailIcon,
  Toggle,
  UiButton,
  Float,
} from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import { MenuList, MenuListItem } from '@/shared/components/Menu'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { graphql } from '@/shared/graphql/router'
import {
  InviteType,
  type InviteInput,
  type ReportQuery,
} from '@/shared/graphql/router/graphql'
import type { Reference, StoreObject } from '@apollo/client'
import type { Entity as LegacyEntity } from '@/shared/graphql/legacy/generated'
import SearchMultipleUsers from '@/features/search/components/SearchMultipleUsers.vue'
import { EMAIL_REGEX } from '@/shared/utils/string'

type ReportViewerEdge = NonNullable<
  NonNullable<NonNullable<ReportQuery['report']>['viewers']>['edges'][number]
>
type ReportViewerNode = ReportViewerEdge['node']
type ShareReport = NonNullable<ReportQuery['report']>

const ADD_REPORT_VIEWERS_MUTATION = graphql(`
  mutation AddReportViewers($input: AddReportViewersInput!) {
    addReportViewers(input: $input) {
      error {
        message
      }
      report {
        id
        settings {
          isPrivate
        }
        viewers(input: { first: 100, filterBy: ALL }) {
          totalCount
          edges {
            cursor
            role
            node {
              ... on User {
                id
                name
                username
                avatar {
                  url
                }
              }
              ... on Organization {
                id
                name
                avatar {
                  url
                }
                owner {
                  ... on User {
                    id
                    name
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  }
`)

const REMOVE_REPORT_VIEWER_MUTATION = graphql(`
  mutation RemoveReportViewer($input: RemoveReportViewerInput!) {
    removeReportViewer(input: $input) {
      success
    }
  }
`)

const UPDATE_REPORT_SETTINGS_MUTATION = graphql(`
  mutation UpdateReportSettings($input: UpdateReportSettingsInput!) {
    updateReportSettings(input: $input) {
      report {
        id
        settings {
          isPrivate
        }
      }
    }
  }
`)

const props = defineProps<{
  report: ShareReport
  getReport?: () => ShareReport | undefined
  publicUrl?: string
}>()

const toastStore = useToastStore()
const { client: apolloClient } = useApolloClient('router')
const report = computed(() => props.getReport?.() ?? props.report)

const usersToInvite = ref<LegacyEntity[]>([])
const publicLinkEnabled = ref(!report.value.settings?.isPrivate)
const copied = ref(false)
const removingViewerKey = ref<string>()
let copiedTimeout: ReturnType<typeof setTimeout> | undefined

const { mutate: addReportViewers, loading: sendInviteLoading } = useMutation(
  ADD_REPORT_VIEWERS_MUTATION,
  { clientId: 'router' }
)

const { mutate: removeReportViewer } = useMutation(
  REMOVE_REPORT_VIEWER_MUTATION,
  { clientId: 'router' }
)

const { mutate: updateReportSettings, loading: updateSettingsLoading } =
  useMutation(UPDATE_REPORT_SETTINGS_MUTATION, { clientId: 'router' })

watch(
  () => report.value.settings?.isPrivate,
  (isPrivate) => {
    if (isPrivate === undefined) return
    publicLinkEnabled.value = !isPrivate
  }
)

const viewerEdges = computed<ReportViewerEdge[]>(
  () => report.value.viewers?.edges ?? []
)

const publicUrl = computed(
  () =>
    props.publicUrl ??
    `${window.location.origin}/infringement/report/${report.value.id}`
)

const getInviteType = (user: LegacyEntity) => {
  if (EMAIL_REGEX.test(user.id)) return InviteType.Email
  return user.__typename === 'BasicUser' ? InviteType.User : InviteType.Org
}

const buildInviteInput = (user: LegacyEntity): InviteInput => ({
  ...(getInviteType(user) === InviteType.Email
    ? { email: user.id }
    : { id: user.id }),
  type: getInviteType(user),
})

const buildViewerInviteInput = (node: ReportViewerNode): InviteInput => {
  if (isEmailInvite(node)) {
    return { email: node.email, type: InviteType.Email }
  }

  return {
    id: node.id,
    type: node.__typename === 'Organization' ? InviteType.Org : InviteType.User,
  }
}

const isEmailInvite = (
  item: ReportViewerNode
): item is Extract<ReportViewerNode, { email: string }> => 'email' in item

const getViewerKey = (item: ReportViewerNode) =>
  isEmailInvite(item) ? item.email : item.id

const getViewerName = (item: ReportViewerNode) =>
  isEmailInvite(item) ? item.email : item.name || 'Unknown'

const getViewerAvatarUrl = (item: ReportViewerNode) =>
  isEmailInvite(item) ? undefined : item.avatar.url

const getViewerSecondaryText = (item: ReportViewerNode) => {
  if (isEmailInvite(item)) return ''
  if ('username' in item) return item.username || ''
  return 'owner' in item && item.owner?.__typename === 'User'
    ? item.owner.name
    : ''
}

const getInviteErrorMessage = (message?: string | null) =>
  message || 'Something went wrong - please try again later'

const removeViewerFromCache = (viewer: ReportViewerNode) => {
  const reportCacheId = apolloClient.cache.identify({
    __typename: 'Report',
    id: report.value.id,
  })

  if (!reportCacheId) return

  const viewerKey = getViewerKey(viewer)

  apolloClient.cache.modify({
    id: reportCacheId,
    fields: {
      viewers(existingViewers = {}, { readField }) {
        const existing = existingViewers as {
          totalCount?: number
          edges?: ReadonlyArray<Reference | StoreObject>
        }

        if (!existing.edges) return existingViewers

        return {
          ...existing,
          totalCount:
            typeof existing.totalCount === 'number'
              ? Math.max(existing.totalCount - 1, 0)
              : existing.totalCount,
          edges: existing.edges.filter((edge) => {
            const node = readField<Reference | StoreObject>('node', edge)
            if (!node) return true

            const key =
              readField<string>('email', node) ?? readField<string>('id', node)

            return key !== viewerKey
          }),
        }
      },
    },
  })
}

const copyrightImage = computed(() => {
  const url = report.value.copyright.asset?.previewImageUrl
  return url ? applyAuthorizationToken(url) : undefined
})

const infringingImage = computed(() => {
  const url = report.value.infringingFile.asset?.previewImageUrl
  return url ? applyAuthorizationToken(url) : undefined
})

const copyrightName = computed(
  () =>
    report.value.copyright.metadata?.originalFilename ||
    report.value.copyright.name
)

const infringingName = computed(() => report.value.infringingFile.name)

const handleSendInvite = async () => {
  if (!usersToInvite.value.length) return

  try {
    const response = await addReportViewers({
      input: {
        reportId: report.value.id,
        invites: usersToInvite.value.map(buildInviteInput),
      },
    })

    const error = response?.data?.addReportViewers.error

    if (error) {
      throw new Error(getInviteErrorMessage(error.message))
    }

    usersToInvite.value = []

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Invites were successfully sent',
    })
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content:
        error instanceof Error
          ? error.message
          : 'Something went wrong - please try again later',
      error: error as Error,
    })
  }
}

const handleRemove = async (viewer: ReportViewerNode) => {
  document.body.click()

  try {
    removingViewerKey.value = getViewerKey(viewer)
    const response = await removeReportViewer({
      input: {
        reportId: report.value.id,
        invite: buildViewerInviteInput(viewer),
      },
    })

    if (response?.data?.removeReportViewer.success) {
      removeViewerFromCache(viewer)
    }
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later',
      error: error as Error,
    })
  } finally {
    removingViewerKey.value = undefined
  }
}

const handlePublicLinkToggle = async (value: boolean) => {
  const previousValue = publicLinkEnabled.value
  publicLinkEnabled.value = value

  try {
    await updateReportSettings({
      input: {
        id: report.value.id,
        isPrivate: !value,
      },
    })
  } catch (error) {
    publicLinkEnabled.value = previousValue
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later',
      error: error as Error,
    })
  }
}

const handleCopyLink = async () => {
  if (copiedTimeout) clearTimeout(copiedTimeout)
  await navigator.clipboard.writeText(publicUrl.value)
  copied.value = true
  copiedTimeout = setTimeout(() => {
    copied.value = false
  }, 1500)
}
</script>
